import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  InputAdornment,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Button,
  ButtonGroup,
  FormControl,
  Select,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import * as XLSX from 'xlsx';
import { getApprovalPersonInvoices } from './functions/get_approval_person_invoices.function';
import {
  approveInvoice,
  rejectInvoice,
  stopInvoice,
} from './functions/update_invoice_status.function';
import { notify } from '../../common/functions/react-toastify.function';
import styles from './approval_persons_invoice_management.module.css';
import dayjs from 'dayjs';

export const DetailsTab = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [invoiceData, setInvoiceData] = useState([]);
  const [originalInvoiceData, setOriginalInvoiceData] = useState([]); // Store original API response
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // { invoiceId, type: 'approve'|'reject'|'stop' }
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    invoiceId: null,
    invoiceNumber: null,
    reason: '',
  });

  // Get unique values for filters from invoice data
  const vendors = useMemo(() => {
    const uniqueVendors = [...new Set(invoiceData.map((item) => item.vendor).filter(Boolean))];
    return uniqueVendors.sort();
  }, [invoiceData]);

  const statuses = useMemo(() => {
    const uniqueStatuses = [...new Set(invoiceData.map((item) => item.status).filter(Boolean))];
    return uniqueStatuses.sort();
  }, [invoiceData]);

  // Fetch approval person invoices (defined first so approve/reject handlers can use it)
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getApprovalPersonInvoices();

      const mappedData = Array.isArray(response)
        ? response.map((invoice) => {
            const formatDate = (dateString) => {
              if (!dateString) {
                return '';
              }
              try {
                const date = new Date(dateString);
                return date.toISOString().split('T')[0];
              } catch (e) {
                return dateString;
              }
            };
            const statusLabel = (status) => {
              if (!status) {
                return '';
              }
              if (status.toLowerCase() === 'pending_rejection') {
                return 'Rejection Pending';
              }
              return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
            };
            return {
              id: invoice.id || Math.random(),
              invoiceNumber: invoice.invoice_number || invoice.invoiceNumber || '',
              invoiceDate: formatDate(
                invoice.created_at || invoice.invoice_date || invoice.invoiceDate
              ),
              vendor: invoice.vendor || '',
              milestone:
                invoice.payment_milestone || invoice.paymentMilestone || invoice.milestone || '',
              invoiceAmount: invoice.invoice_amount || invoice.invoiceAmount || invoice.amount || 0,
              poNumber: invoice.po_number || invoice.poNumber || '',
              statusRaw: (invoice.status || '').toLowerCase(),
              status: statusLabel(invoice.status || ''),
              assignedApprover: invoice.assigned_role?.name || '',
              previousApprover: invoice.previous_assigned_role?.name || '',
              region: invoice.region || invoice.assigned_role?.region || '',
              reasonOfRejection: invoice.reason_of_rejection || '',
              _originalData: invoice,
            };
          })
        : [];

      const originalData = Array.isArray(response) ? response : [];
      setInvoiceData(mappedData);
      setOriginalInvoiceData(originalData);
    } catch (error) {
      console.error('Error fetching approval person invoices:', error);
      setInvoiceData([]);
      setOriginalInvoiceData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Approve invoice from table row
  const handleApprove = useCallback(
    async (invoiceId, invoiceNumberForUi) => {
      if (!invoiceId || actionLoading) {
        return;
      }
      setActionLoading({ invoiceId, invoiceNumber: invoiceNumberForUi, type: 'approve' });
      try {
        await approveInvoice(invoiceId, '');
        notify('success', 'Invoice approved successfully');
        await fetchInvoices();
      } catch (err) {
        // Error handled in approveInvoice
      } finally {
        setActionLoading(null);
      }
    },
    [actionLoading, fetchInvoices]
  );

  // Open reject dialog, then confirm calls handleRejectConfirm
  const handleRejectClick = useCallback((rowData) => {
    const invoiceId = rowData?._originalData?.id ?? rowData?.id ?? null;
    const invoiceNumber = rowData?.invoiceNumber;
    if (!invoiceId) {
      return;
    }
    setRejectDialog({
      open: true,
      invoiceId,
      invoiceNumber,
      // Autofill existing reason (e.g. pending_rejection forwarded to Planning L1)
      reason: rowData?.reasonOfRejection || '',
    });
  }, []);

  const handleRejectDialogClose = useCallback(() => {
    setRejectDialog({ open: false, invoiceId: null, invoiceNumber: null, reason: '' });
  }, []);

  const handleRejectConfirm = useCallback(async () => {
    const { invoiceId, invoiceNumber, reason } = rejectDialog;
    if (!invoiceId || actionLoading) {
      return;
    }
    const trimmed = (reason || '').trim();
    if (!trimmed) {
      notify('error', 'Reason of rejection is required');
      return;
    }
    setActionLoading({ invoiceId, invoiceNumber, type: 'reject' });
    try {
      await rejectInvoice(invoiceId, trimmed, '');
      notify('success', 'Invoice rejected');
      handleRejectDialogClose();
      await fetchInvoices();
    } catch (err) {
      // Error handled in rejectInvoice
    } finally {
      setActionLoading(null);
    }
  }, [rejectDialog, actionLoading, fetchInvoices, handleRejectDialogClose]);

  // Stop invoice (Planning L4 / L5 only) - from table row
  const handleStop = useCallback(
    async (invoiceId, invoiceNumberForUi) => {
      if (!invoiceId || actionLoading) {
        return;
      }
      setActionLoading({ invoiceId, invoiceNumber: invoiceNumberForUi, type: 'stop' });
      try {
        await stopInvoice(invoiceId, '');
        notify('success', 'Invoice stopped');
        await fetchInvoices();
      } catch (err) {
        // Error handled in stopInvoice
      } finally {
        setActionLoading(null);
      }
    },
    [actionLoading, fetchInvoices]
  );

  // Open invoice details (used by both invoice number click and three-dots menu)
  const handleOpenInvoiceDetails = useCallback(
    (rowData) => {
      const invoiceNumber = rowData.invoiceNumber;
      const fullInvoiceData = rowData._originalData;

      if (!fullInvoiceData) {
        const foundData = originalInvoiceData.find(
          (inv) => (inv.invoice_number || inv.invoiceNumber) === invoiceNumber
        );
        if (foundData) {
          navigate(`/approvalPersonsInvoiceManagement/invoiceDetails/${invoiceNumber}`, {
            state: { invoiceData: foundData },
          });
        } else {
          navigate(`/approvalPersonsInvoiceManagement/invoiceDetails/${invoiceNumber}`);
        }
        return;
      }

      navigate(`/approvalPersonsInvoiceManagement/invoiceDetails/${invoiceNumber}`, {
        state: { invoiceData: fullInvoiceData },
      });
    },
    [navigate, originalInvoiceData]
  );

  // Actions Menu Component
  // eslint-disable-next-line react/prop-types
  const ActionsMenu = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleViewDetails = () => {
      handleClose();
      handleOpenInvoiceDetails(row.original);
    };

    return (
      <>
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{
            color: '#667085',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            onClick={handleViewDetails}
            sx={{
              gap: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemText primary="View Details" />
          </MenuItem>
        </Menu>
      </>
    );
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice Number',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const invoiceNumber = cell.getValue();
          const rowData = cell.row.original;
          return (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenInvoiceDetails(rowData);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenInvoiceDetails(rowData);
                }
              }}
              style={{
                fontSize: '14px',
                color: '#1976d2',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#1565c0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#1976d2';
              }}
            >
              {invoiceNumber || 'N/A'}
            </span>
          );
        },
      },
      {
        accessorKey: 'invoiceDate',
        header: 'Invoice Date',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const date = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{formatDate(date)}</span>;
        },
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        size: 250,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const vendor = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{vendor || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'reasonOfRejection',
        header: 'Reason of Rejection',
        size: 240,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{v ? String(v) : '—'}</span>;
        },
      },
      {
        accessorKey: 'milestone',
        header: 'Milestone',
        size: 120,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const milestone = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{milestone || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'invoiceAmount',
        header: 'Invoice Amount',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const value = cell.getValue();
          return value
            ? `${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '-';
        },
      },
      {
        accessorKey: 'poNumber',
        header: 'PO Number',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const poNumber = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{poNumber || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'assignedApprover',
        header: 'Assigned Approver',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const approver = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{approver || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'previousApprover',
        header: 'Previous Approver',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const approver = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{approver || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'region',
        header: 'Region',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const region = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{region || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const status = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{status || 'N/A'}</span>;
        },
      },
      {
        header: 'Approve / Reject',
        size: 200,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => {
          const invoiceNumber = row.original.invoiceNumber;
          const invoiceId = row.original._originalData?.id ?? row.original.id ?? null;
          const assignedApprover = (row.original.assignedApprover || '').toLowerCase().trim();
          const status = (row.original.statusRaw || '').toLowerCase();
          const isPlanningL4OrL5 =
            assignedApprover === 'planning l4' || assignedApprover === 'planning l5';
          const isPending = status === 'pending' || status === 'pending_rejection';
          const showStopButton = isPlanningL4OrL5 && isPending;
          const isApproving =
            actionLoading?.invoiceId === invoiceId && actionLoading?.type === 'approve';
          const isRejecting =
            actionLoading?.invoiceId === invoiceId && actionLoading?.type === 'reject';
          const isStopping =
            actionLoading?.invoiceId === invoiceId && actionLoading?.type === 'stop';
          const anyLoading = isApproving || isRejecting || isStopping;
          return (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'stretch',
                borderRadius: 1.5,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50',
              }}
            >
              {showStopButton && (
                <Tooltip title="Stop invoice">
                  <Button
                    size="small"
                    variant="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStop(invoiceId, invoiceNumber);
                    }}
                    disabled={anyLoading}
                    sx={{
                      px: 1.25,
                      minWidth: 0,
                      color: '#BD1D23',
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderRadius: 0,
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { backgroundColor: 'rgba(189, 29, 35, 0.08)' },
                    }}
                  >
                    {isStopping ? <CircularProgress size={14} color="inherit" /> : 'Stop'}
                  </Button>
                </Tooltip>
              )}
              <ButtonGroup
                variant="text"
                size="small"
                disableElevation
                sx={{
                  '& .MuiButtonGroup-grouped': {
                    border: 'none',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                    px: 1.25,
                    minWidth: 0,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                  },
                }}
              >
                <Tooltip title="Approve">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApprove(invoiceId, invoiceNumber);
                    }}
                    disabled={anyLoading || !invoiceId}
                    sx={{
                      color: '#2e7d32',
                      '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' },
                    }}
                  >
                    {isApproving ? <CircularProgress size={14} color="inherit" /> : 'Approve'}
                  </Button>
                </Tooltip>
                <Tooltip title="Reject">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRejectClick(row.original);
                    }}
                    disabled={anyLoading || !invoiceId}
                    sx={{
                      color: '#d32f2f',
                      '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
                    }}
                  >
                    {isRejecting ? <CircularProgress size={14} color="inherit" /> : 'Reject'}
                  </Button>
                </Tooltip>
              </ButtonGroup>
            </Box>
          );
        },
      },
      {
        header: 'Actions',
        enableColumnActions: false,
        size: 50,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => <ActionsMenu row={row} />,
      },
    ],
    [handleOpenInvoiceDetails, handleApprove, handleRejectClick, handleStop, actionLoading]
  );

  // Filter data based on search term and filters
  const filteredData = useMemo(() => {
    let filtered = invoiceData;

    // Filter only pending / pending_rejection invoices (Action Required tab)
    filtered = filtered.filter((item) => {
      const status = (item.statusRaw || '').toLowerCase();
      return status === 'pending' || status === 'pending_rejection';
    });

    // Filter by invoice date (from/to)
    if (dateFrom || dateTo) {
      const from = dateFrom ? dayjs(dateFrom).startOf('day') : null;
      const to = dateTo ? dayjs(dateTo).endOf('day') : null;
      filtered = filtered.filter((item) => {
        if (!item.invoiceDate) {
          return false;
        }
        const itemDate = dayjs(item.invoiceDate);
        if (!itemDate.isValid()) {
          return false;
        }
        if (from && itemDate.isBefore(from)) {
          return false;
        }
        if (to && itemDate.isAfter(to)) {
          return false;
        }
        return true;
      });
    }

    // Filter by vendor
    if (selectedVendor) {
      filtered = filtered.filter((item) => item.vendor === selectedVendor);
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter((item) => item.statusRaw === selectedStatus);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    return filtered;
  }, [searchTerm, dateFrom, dateTo, selectedVendor, selectedStatus, invoiceData]);

  // Reset pagination to first page when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchTerm, dateFrom, dateTo, selectedVendor, selectedStatus]);

  // Download Excel function
  const handleDownloadExcel = (tableInstance) => {
    // Get filtered rows from the table instance (includes all filters: custom + MUI column filters)
    // If table instance is provided, use filtered rows; otherwise fallback to filteredData
    let dataToDownload = filteredData;

    if (tableInstance) {
      const filteredRows = tableInstance.getFilteredRowModel().rows;
      dataToDownload = filteredRows.map((row) => row.original);
    }

    if (dataToDownload.length === 0) {
      return;
    }

    try {
      const excelData = dataToDownload.map((item) => ({
        'Invoice Number': item.invoiceNumber || '',
        'Invoice Date': item.invoiceDate ? formatDate(item.invoiceDate) : '',
        Vendor: item.vendor || '',
        Milestone: item.milestone || '',
        'Invoice Amount': item.invoiceAmount || '',
        'PO Number': item.poNumber || '',
        'Assigned Approver': item.assignedApprover || '',
        'Previous Approver': item.previousApprover || '',
        Region: item.region || '',
        Status: item.status || '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice Details');

      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Invoice_Details_${currentDate}.xlsx`;

      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
    }
  };

  const isPendingRejectionRow = (row) => {
    const raw = (row?.original?.statusRaw || '').toLowerCase();
    if (raw === 'pending_rejection') {
      return true;
    }
    const display = (row?.original?.status || '').trim().toLowerCase();
    return display === 'rejection pending';
  };

  const table = useMaterialReactTable({
    columns,
    data: Array.isArray(filteredData) ? filteredData : [],
    state: {
      isLoading: loading,
      pagination,
    },
    onPaginationChange: setPagination,
    muiCircularProgressProps: {
      color: 'inherit',
      size: 40,
      sx: { color: '#999999' },
    },
    enableGlobalFilter: false,
    enableGlobalFilterModes: false,
    positionGlobalFilter: 'none',
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableRowSelection: false,
    enableSelectAll: false,
    enableColumnPinning: false,
    enableFacetedValues: false,
    enableSorting: false,
    enableDensityToggle: false,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    manualPagination: false,
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30, 40],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        height: 52,
        minHeight: 52,
        maxHeight: 52,
        ...(isPendingRejectionRow(row)
          ? {
              backgroundColor: 'rgba(11, 245, 169, 0.10)',
              '&:hover': { backgroundColor: 'rgba(11, 245, 169, 0.16)' },
            }
          : {}),
      },
    }),
    muiTableBodyCellProps: ({ row }) => {
      const highlight = isPendingRejectionRow(row);
      return {
        sx: {
          color: '#667085',
          backgroundColor: highlight ? 'rgba(245, 158, 11, 0.10)' : '#fff',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
          padding: '0 12px',
          ...(highlight ? { '&:hover': { backgroundColor: 'rgba(11, 245, 169, 0.10)' } } : {}),
          '&:first-of-type': {
            paddingLeft: '12px !important',
          },
        },
      };
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
        height: '56px',
        padding: '0 12px',
        alignItems: 'center',
        '&:first-of-type': {
          paddingLeft: '18px !important',
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        height: 56,
        '& .MuiTableCell-root': {
          padding: '6px 12px',
          verticalAlign: 'middle',
        },
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        {/* Left side - Search field */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search"
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            sx={{
              backgroundColor: '#fff',
              width: '300px',
              '& .MuiInputBase-input': {
                color: 'gray',
                '::placeholder': {
                  color: '#b3b9c4',
                  opacity: 1,
                },
              },
              '& .MuiOutlinedInput-root': {
                border: '1px solid #ccc',
                borderRadius: '10px',
                paddingRight: 0,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Right side - Filter dropdowns and Download button */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Invoice date from/to with Clear button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Invoice date from"
                value={dateFrom ? dayjs(dateFrom) : null}
                onChange={(v) => setDateFrom(v ? v.toDate() : null)}
                format="DD-MM-YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    sx: {
                      minWidth: 168,
                      backgroundColor: '#fff',
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                    },
                  },
                }}
              />
              <DatePicker
                label="Invoice date to"
                value={dateTo ? dayjs(dateTo) : null}
                onChange={(v) => setDateTo(v ? v.toDate() : null)}
                format="DD-MM-YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    sx: {
                      minWidth: 168,
                      backgroundColor: '#fff',
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            {(dateFrom || dateTo) && (
              <IconButton
                size="small"
                onClick={() => {
                  setDateFrom(null);
                  setDateTo(null);
                }}
                sx={{
                  padding: '4px',
                  color: '#757575',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  border: '1px solid #ccc',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    color: '#333',
                    border: '1px solid #999',
                  },
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Vendor Dropdown */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                VENDOR
              </MenuItem>
              {vendors.map((vendor, index) => (
                <MenuItem
                  key={vendor}
                  value={vendor}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== vendors.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {vendor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Dropdown */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                STATUS
              </MenuItem>
              {statuses.map((status, index) => (
                <MenuItem
                  key={status}
                  value={status}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== statuses.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Download button */}
          <Button
            variant="outlined"
            onClick={() => handleDownloadExcel(table)}
            disabled={filteredData.length === 0}
            sx={{
              backgroundColor: 'transparent',
              border: '1px solid #ccc',
              color: '#333',
              borderRadius: '10px',
              minWidth: 'auto',
              padding: '8px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0,0, 0.04)',
                border: '1px solid #ccc',
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                color: '#999',
                opacity: 0.6,
              },
            }}
          >
            <DownloadIcon />
          </Button>
        </Box>
      </Box>
    ),
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <MaterialReactTable table={table} />
      <Dialog open={rejectDialog.open} onClose={handleRejectDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Reason of rejection is required.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Reason of rejection"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleRejectDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={actionLoading?.type === 'reject'}
          >
            {actionLoading?.type === 'reject' ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
